import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AuthDataDto, RegisterDto, authState } from "./auth.types";
import authService from "./authService";
import { AvailableAPIProvidersType } from "../apiURLS";
import type { RootState } from "../../app/store";
import { AuthErrorPayload, toAuthError } from "./socialLogin";
import { clearCache } from "../../app/offlineCache";

//Get user from sessionStorage

const userStorage = localStorage.getItem("authData");

const user: AuthDataDto = userStorage !== null ? JSON.parse(userStorage) : null;

const initialState: authState = {
  authData: user ? user : null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
  isAuthorized: true,
};

//Register new user
export const register = createAsyncThunk<
  AuthDataDto,
  RegisterDto,
  { state: RootState; rejectValue: AuthErrorPayload }
>("auth/register", async (registerInfo, thunkAPI) => {
  try {
    return await authService.register(registerInfo);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(toAuthError(error));
  }
});

/**
 * Login new user
 * @param email User credentials for logging in
 * @param password User credentials for logging in
 * @returns user Logged-in user data
 */
export const login = createAsyncThunk<
  AuthDataDto,
  any,
  { state: RootState; rejectValue: AuthErrorPayload }
>("auth/login", async (loginCredentials, thunkAPI) => {
  try {
    return await authService.login(loginCredentials);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(toAuthError(error));
  }
});

/**
 * Refresh user access token
 * @returns Refreshed access token data
 */
export interface RefreshError {
  message: string;
  /** HTTP status; undefined when the server wasn't reached */
  status?: number;
}

export const refreshAccess = createAsyncThunk<
  AuthDataDto, // Payload returned by the thunk
  void, // No input parameters required
  { state: RootState; rejectValue: RefreshError } // ThunkAPI type
>("auth/refresh", async (_, thunkAPI) => {
  try {
    const response = await authService.refreshAccess();
    // Ensure the new access token is stored securely
    if (response?.token) {
      localStorage.setItem("daex-auth-token", response.token);
    }
    return response; // Return the refreshed token data
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();

    // The status lets callers tell "logged out" from "try again later";
    // the service already removed the token if the session is invalid
    return thunkAPI.rejectWithValue({
      message,
      status: error.response?.status,
    });
  }
});

// Thunk to handle Google login
export const googleIDLogin = createAsyncThunk<
  AuthDataDto,
  string,
  { state: RootState; rejectValue: AuthErrorPayload }
>("auth/google-login", async (accessToken, thunkAPI) => {
  try {
    return await authService.googleIDLogin(accessToken);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(toAuthError(error));
  }
});

export const loginWithAPI = createAsyncThunk<
  AuthDataDto,
  {
    provider: AvailableAPIProvidersType;
    providerToken: string;
    codeVerifier: string;
    redirectUri?: string;
  },
  { state: RootState; rejectValue: AuthErrorPayload }
>(
  "auth/loginWithAPI",
  async ({ provider, providerToken, codeVerifier, redirectUri }, thunkAPI) => {
    try {
      return await authService.loginWithAPI(
        providerToken,
        provider,
        codeVerifier,
        redirectUri,
      );
    } catch (error: any) {
      const authError = toAuthError(error);
      console.error(authError.message);
      return thunkAPI.rejectWithValue(authError);
    }
  },
);

export const linkSocialAccount = createAsyncThunk<
  AuthDataDto,
  { linkToken: string; password?: string; dropPassword?: boolean },
  { state: RootState; rejectValue: AuthErrorPayload }
>("auth/linkSocialAccount", async (data, thunkAPI) => {
  try {
    return await authService.linkSocialAccount(data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(toAuthError(error));
  }
});

//Logout user

export const logout = createAsyncThunk<void, void, { state: RootState }>(
  "auth/logout",
  async () => {
    await Promise.all([authService.logout(), clearCache()]);
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuth: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.isError = false;
      state.isAuthorized = true;
    },
    setUnauthorized: (state) => {
      state.isAuthorized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      //register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.authData = null;
        state.isError = true;
        state.message = action.payload?.message ?? "";
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      //login
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.authData = null;
        state.isError = true;
        state.message = action.payload?.message ?? "";
      })
      .addCase(refreshAccess.pending, (state) => {
        state.isLoading = true;
      })
      //login
      .addCase(refreshAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(refreshAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.authData = null;
        state.isError = true;
        state.message = action.payload?.message ?? action.error.message ?? "";
      })
      .addCase(googleIDLogin.pending, (state) => {
        state.isLoading = true;
      })
      //login
      .addCase(googleIDLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(googleIDLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.authData = null;
        state.isError = true;
        state.message = action.payload?.message ?? "";
      })

      .addCase(loginWithAPI.pending, (state) => {
        state.isLoading = true;
      })
      //login
      .addCase(loginWithAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(loginWithAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.authData = null;
        state.isError = true;
        state.message = action.payload?.message ?? "";
      })
      .addCase(linkSocialAccount.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(linkSocialAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.authData = action.payload;
      })
      .addCase(linkSocialAccount.rejected, (state) => {
        // The link page shows its own errors
        state.isLoading = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.authData = null;
      });
  },
});

export const { resetAuth, setUnauthorized } = authSlice.actions;
export default authSlice.reducer;
